import { CSSObject } from '@emotion/react'
import classnames from 'classnames'
import { produce } from 'immer'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { UserLike, InheritanceResource } from '@opallabs/concepts'
import type { MultiSelectDiffObject } from '@opallabs/hooks'
import { InputLabel, WithStyleOverrides } from '@opallabs/lapidary'
import { getMemberCount } from '../helpers'
import { SearchableUserList } from '../SearchableUserList'
import { CollaboratorListItem } from './CollaboratorListItem'
import { ResourceListItem } from './ResourceListItem'
import * as styles from './styles'

export enum PermissionLevels {
  owner = 'Owner',
  collaborator = 'Can Access',
}

export type CollaboratorPermissionListProps<TUserLike> = WithStyleOverrides & {
  currentUserUuid: string
  hideCollaborators?: boolean
  inheritanceResources?: InheritanceResource[]
  initialCollaborators?: TUserLike[]
  inputLabel?: string
  maxVisibleList?: number
  noCollaboratorsText?: string
  onAddCollaborator?: (collaborator: TUserLike) => void
  onChangeInheritance?: (
    resource: InheritanceResource,
    inheritsMembership: boolean
  ) => void
  onCloseUserSearch?: (selectionDiff: MultiSelectDiffObject<TUserLike>) => void
  onRemoveCollaborator?: (
    collaborator: TUserLike,
    focusOutsideElement?: boolean
  ) => void
  owner?: Extract<TUserLike, { type: 'user' }>
  readOnly?: boolean
  /* Restrict search results to users in your workspace. Defaults to `false`. */
  restrictToWorkspace?: UserLike['type'][]
  searchPlaceholder?: string
  showCount?: boolean
  showPermissionLabel?: boolean
  updateInitialValueOnPropChange?: boolean
  userCanRemoveSelf?: boolean
  width?: string | number
  showUserGroups?: boolean
}

export const CollaboratorPermissionList = <TUserLike extends UserLike>({
  currentUserUuid,
  hideCollaborators = false,
  inheritanceResources = [],
  initialCollaborators = [],
  inputLabel = 'Collaborators',
  maxVisibleList,
  noCollaboratorsText,
  onAddCollaborator,
  onChangeInheritance,
  onCloseUserSearch,
  onRemoveCollaborator,
  owner,
  readOnly = false,
  restrictToWorkspace = [],
  searchPlaceholder,
  showCount = false,
  showPermissionLabel = true,
  styleOverrides,
  updateInitialValueOnPropChange = false,
  userCanRemoveSelf = true,
  width = 354,
  showUserGroups = false,
}: CollaboratorPermissionListProps<TUserLike>) => {
  const collaboratorListRef = useRef<HTMLUListElement | null>(null)

  const [collaborators, setCollaborators] =
    useState<TUserLike[]>(initialCollaborators)
  const hasItems =
    (owner ? 1 : 0) + collaborators.length + inheritanceResources.length > 0

  const sortedCollaborators = useMemo(() => {
    return produce<readonly (TUserLike | InheritanceResource)[]>(
      collaborators,
      (draftState) => {
        const ownerIndex = owner
          ? draftState.findIndex(({ uuid }) => uuid === owner.uuid)
          : -1
        // Remove owner from list, as they're rendered separately
        if (ownerIndex > -1) {
          draftState.splice(ownerIndex, 1)
        }

        const currentUserIndex =
          // If owner is also the current user, this lookup is ignored
          // in favor of the above owner lookup
          !owner || owner.uuid !== currentUserUuid
            ? draftState.findIndex(({ uuid }) => uuid === currentUserUuid)
            : -1
        // Move current user to start of list
        if (currentUserIndex > -1) {
          draftState.unshift(draftState.splice(currentUserIndex, 1)[0])
        }

        // Append inheritance resources after all users
        draftState.push(...inheritanceResources)
      }
    )
  }, [currentUserUuid, collaborators, inheritanceResources, owner])

  const removeFromState = (collaborator: TUserLike) => {
    const updatedCollaborators = collaborators.filter(
      (user) => collaborator.uuid !== user.uuid
    )
    setCollaborators(updatedCollaborators)
  }

  const onSelectCollaborator = (user: TUserLike, action: 'add' | 'remove') => {
    if (action === 'add') {
      setCollaborators([...collaborators, user])
      return onAddCollaborator?.(user)
    }

    removeFromState(user)
    return onRemoveCollaborator?.(user)
  }

  const focusNextCollaborator = (index: number) => {
    if (!collaboratorListRef.current) return
    const listItems = collaboratorListRef.current.children
    // add 1 to index to account for separate owner list entry
    const nextListItem = listItems[index + (owner ? 1 : 0)]
    const nextRemoveButton =
      nextListItem.lastElementChild?.getElementsByTagName('button')[0]
    nextRemoveButton?.focus()
  }

  const removeFromList = ({
    collaborator,
    index,
    byKeyPress,
  }: {
    collaborator: TUserLike
    index: number
    byKeyPress: boolean
  }) => {
    const isLastElement = index === collaborators.length - 1
    let focusOutsideElement = false
    if (byKeyPress && isLastElement) {
      focusOutsideElement = true
    }
    removeFromState(collaborator)
    onRemoveCollaborator?.(collaborator, focusOutsideElement)

    if (byKeyPress && !isLastElement && !focusOutsideElement) {
      // plus 1 because rendering removing the item will not have happened yet
      focusNextCollaborator(index + 1)
    }
  }

  useEffect(() => {
    if (updateInitialValueOnPropChange) {
      setCollaborators(initialCollaborators)
    }
  }, [updateInitialValueOnPropChange, initialCollaborators])

  const [memberCount] = useMemo(
    () => getMemberCount(owner ? [...collaborators, owner] : collaborators),
    [collaborators, owner]
  )

  return (
    <div css={styleOverrides}>
      {!hideCollaborators && (
        <span css={styles.labelWrapper}>
          <InputLabel css={styles.label} text={inputLabel} />
          {showCount && <span css={styles.count}>{memberCount}</span>}
        </span>
      )}
      <div
        css={styles.container({
          hideCollaborators: hideCollaborators || readOnly,
          listLength: collaborators.length,
          maxVisibleList,
          styleOverrides: styleOverrides as CSSObject | undefined,
        })}
      >
        {!hideCollaborators && !readOnly && (
          <SearchableUserList<TUserLike>
            label="Select collaborators"
            excludedUsers={owner ? [owner] : undefined}
            initialSelection={collaborators}
            onMenuCloseDiff={onCloseUserSearch}
            onSelectionChange={onSelectCollaborator}
            placeholder={searchPlaceholder}
            restrictToWorkspace={restrictToWorkspace}
            showUserGroups={showUserGroups}
            width={typeof width === 'number' ? `${width}px` : width}
          />
        )}
        <ul
          className={classnames('selected-collaborator-list')}
          aria-label="Selected collaborators"
          ref={collaboratorListRef}
          tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        >
          {owner && (
            <CollaboratorListItem
              collaborator={owner}
              key={owner.uuid}
              onRemoveCollaborator={() => null}
              permission={PermissionLevels.owner}
              removable={false}
              showPermissions={showPermissionLabel}
            />
          )}
          {!hideCollaborators ? (
            hasItems || !noCollaboratorsText ? (
              sortedCollaborators.map((collaborator, index) => {
                if (collaborator.type === 'resource') {
                  return (
                    <ResourceListItem
                      key={collaborator.uuid}
                      onChangeInheritance={(inheritsMembership) =>
                        onChangeInheritance?.(collaborator, inheritsMembership)
                      }
                      readOnly={readOnly}
                      resource={collaborator}
                      showPermissions={showPermissionLabel}
                    />
                  )
                }
                return (
                  <CollaboratorListItem
                    collaborator={collaborator}
                    key={collaborator.uuid}
                    onRemoveCollaborator={(event, byKeyPress) =>
                      removeFromList({ collaborator, index, byKeyPress })
                    }
                    permission={PermissionLevels.collaborator}
                    readOnly={readOnly}
                    removable={
                      userCanRemoveSelf || currentUserUuid !== collaborator.uuid
                    }
                    showPermissions={showPermissionLabel}
                  />
                )
              })
            ) : (
              <li css={styles.noCollaboratorsText}>{noCollaboratorsText}</li>
            )
          ) : null}
        </ul>
      </div>
      <span css={styles.helpText}>
        Users gain access when added directly or as members of a group or space.
      </span>
    </div>
  )
}
